import { IModalMessageOptions } from '@vivli/shared/infrastructure/interface';
import { useEffect, useRef } from 'react';
import { first } from 'rxjs/operators';
import { useNavigate } from 'react-router';
import {
    useActiveUserContext,
    useAppConfigContext,
    useAuthEventsContext,
    useAuthServiceContext,
    useModalApiContext,
    useOrganizationsApiContext,
    usePortalConfigContext,
    useUsersApiContext
} from '@vivli/core/infrastructure/context';
import { EventType } from '@azure/msal-browser';
import { PopupEvent } from '@azure/msal-browser/dist/event/EventMessage';

export const useAuthEventsInit = () => {
    const navigate = useNavigate();
    const { user } = useActiveUserContext();
    const organizationsApi = useOrganizationsApiContext();
    const modalApi = useModalApiContext();
    const usersApi = useUsersApiContext();
    const appConfig = useAppConfigContext();
    const authEvents = useAuthEventsContext();
    const authService = useAuthServiceContext();
    const { getSearchLink } = usePortalConfigContext();

    const b2cModalRef = useRef<Window>(null);
    const modalIdRef = useRef<string>(null);

    const modalOptions: IModalMessageOptions = {
        showContinueButton: false,
        showLoadingSpinner: true,
        dismissOnClickOutside: false,
        style: {
            padding: '40px 140px 25px 140px',
            borderRadius: '10px'
        },
        messageStyle: {
            color: 'rgb(5, 61, 88)',
            fontSize: '21px',
            letterSpacing: '0.1em'
        },
        onClick: () => showLoginWindow()
    };

    const showLoginWindow = () => {
        b2cModalRef.current?.focus();
    };

    const handleAcceptInvite = () => {
        const acceptingInvite = window.location.href.toLowerCase().indexOf('/acceptinvite') !== -1;
        if (user?.orgMemberships.length === 0 && !acceptingInvite) {
            organizationsApi
                .joinMember()
                .pipe(first())
                .subscribe((orgNames) => {
                    if (orgNames.length) {
                        modalApi.message(`You have been successfully joined to ${orgNames.toString()}`);
                    }
                });
        }
    };

    const handleTrackUserLogin = () => {
        usersApi.trackUserActivity().pipe(first()).subscribe();
    };

    const handleLoginRedirect = () => {
        // TODO - at a less risky time find a better way to do this
        // rather than checking all these "exception" states - verify when
        // redirect to dashboard is REALLY required and do the redirect only in
        // those cases.
        const isAcceptingInvite = window.location.pathname.indexOf('acceptinvite') !== -1; // accepting invite
        const isDashboardPath = window.location.pathname === '/'; // going to dashboard
        const isRequestResults = appConfig.viewRequestResultsOnly; // vm side result requests
        const isSearchQuery = window.location.search.indexOf('search') !== -1; // has search query param
        const isReport = window.location.pathname.indexOf('admin/report') !== -1; // is report
        const isEnquiryLanding = window.location.pathname.indexOf('enquiries/link') !== -1; // enquiry landing page

        // do not redirect if we are accepting an invite, going to the dashboard,
        // viewing request results on VM side, displaying an admin report,
        // and does not contain a search query param
        const redirectToDashboard = !isAcceptingInvite && isDashboardPath && !isRequestResults && !isSearchQuery && !isReport;

        if (redirectToDashboard) {
            navigate('/admin/dashboard');
        } else if (isEnquiryLanding) {
            navigate('/admin/enquiries/');
        }
    };

    const showMessage = (message: string) => {
        // hide other messages before showing a new one
        hideActiveMessage(true);

        // track actively shown message
        modalIdRef.current = modalApi.message(message, modalOptions);
    };

    const hideActiveMessage = (hideAll = false) => {
        if (hideAll) {
            modalApi.hide();
        }

        if (!modalIdRef.current) {
            return;
        }

        modalApi.hide(modalIdRef.current);
        modalIdRef.current = null;
    };

    const handleLogoutWarning = () => {
        const message = 'Your session will expire soon.  Please press OK to extend your log-in; otherwise you will be logged out shortly.';
        modalIdRef.current = modalApi.confirm(message, {
            onConfirm: () => {
                authService.getTokenPopup();
            },
            confirmText: 'OK',
            hideCancel: true
        });
    };

    const tabVisibilityHandler = () => {
        if (!document.hidden && modalIdRef.current) {
            showLoginWindow();
        }
    };

    const handleUserLogin = () => {
        hideActiveMessage();
        handleTrackUserLogin();
        handleAcceptInvite();
        handleLoginRedirect();
    };

    const initializeWindowFocusEvent = () => {
        authService.instance.addEventCallback((message) => {
            if (message.eventType === EventType.POPUP_OPENED) {
                // Save the popup window for focusing later
                const payload = message.payload as PopupEvent;
                b2cModalRef.current = payload.popupWindow as Window;
            }
            return message;
        });
    };

    const initializeAuthEventsHandler = () => {
        return authEvents.subscribe((event) => {
            switch (event) {
                case 'logout':
                    showMessage('Logging out...');
                    break;
                case 'login':
                case 'signup-success':
                    showMessage('Logging in...');
                    break;
                case 'password-update':
                    showMessage(
                        'Updating password...If a new window does not appear, check for a pop-up blocker.  After allowing the pop-up, refresh this screen to complete the password update process.'
                    );
                    break;
                case 'profile-update':
                    showMessage('Updating profile...');
                    break;
                case 'signup':
                    showMessage('Signing up...');
                    break;
                case 'signup-failure':
                case 'login-failure':
                case 'password-update-success':
                case 'profile-update-success':
                    hideActiveMessage();
                    break;
                case 'get-user-info-success':
                    handleUserLogin();
                    break;
                case 'logout-warning':
                    handleLogoutWarning();
                    break;
                case 'logout-success':
                // A failed logout now ends the local session too, so the user-visible outcome is
                // identical: close the "Logging out..." modal and land on the logged-out page.
                case 'logout-failure':
                case 'silent-login-failure':
                    hideActiveMessage(true);
                    navigate(getSearchLink());
                    break;
                case 'cancelled':
                    hideActiveMessage(true);
                    break;
            }
        });
    };

    useEffect(() => {
        initializeWindowFocusEvent();

        const authSub = initializeAuthEventsHandler();

        document.addEventListener('visibilitychange', tabVisibilityHandler);

        return () => {
            authSub.unsubscribe();

            document.removeEventListener('visibilitychange', tabVisibilityHandler);
        };
    }, []);
};
